import {
  alpha,
  Avatar,
  IconButton,
  InputAdornment,
  InputBase,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { useState, useCallback, Fragment, useRef } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import Routes from "constants/routes";
import { useMsal } from "@azure/msal-react";
import React from "react";
import { Article, Cloud, Logout } from "@mui/icons-material";
import { isProduction } from "authConfig";
import MenuIcon from "@mui/icons-material/Menu";
import { DashboardAppDrawer } from "components/Dashboard/AppDrawer";
import { onPaletteModeChanged, PaletteModeToggle, SMPaletteMode } from "@sanmanuel/smkit-react";
import { Restrict, When } from "@sanmanuel/permissions";
import { DashboardPermissions } from "components/Dashboard/DashboardLayout/SortableItem";
import { SMTooltip } from "components/lib/SMTooltip";

const SearchInput = styled(InputBase)(({ theme }) => ({
  position: "relative",
  color: "inherit",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.05),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.1),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 1),
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "55ch",
    },
  },
}));

interface IProps {
  mode: SMPaletteMode;
  onPaletteModeChanged: onPaletteModeChanged;
  mocking: boolean;
  onMockingChanged: (mocking: boolean) => void;
}

const TopNavigation = (props: IProps) => {
  const { mode, onPaletteModeChanged, mocking, onMockingChanged } = props;
  const { instance, accounts } = useMsal();
  const theme = useTheme();
  let navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const searchText = useRef("");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const signOut = () => {
    handleMenuClose();
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };

  const handleSearchParams = useCallback(async () => {
    navigate(Routes.searchResultPathFor(searchText.current));
  }, [navigate, searchText]);

  const handleDrawerToggled = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  return (
    <Fragment>
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Restrict to={DashboardPermissions} when={When.any}>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggled}
            >
              <MenuIcon />
            </IconButton>
          </Restrict>
          <SearchInput
            placeholder="Search for Patron by Club Number, Name, Email, or Phone Number"
            onChange={(e) => {
              searchText.current = e.target.value;
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter" || searchText.current === undefined) return;
              handleSearchParams();
            }}
            startAdornment={
              <InputAdornment position="end">
                <SearchIcon htmlColor="white" />
              </InputAdornment>
            }
          />
          <Box sx={{ flexGrow: 1, minWidth: 2 }} />
          <Stack direction="row" spacing={1}>
            {!isProduction && (
              <IconButton color="inherit" onClick={() => onMockingChanged(!mocking)}>
                <SMTooltip
                  title={
                    <Stack>
                      <Typography textAlign="center" fontSize="inherit">
                        Switch to {mocking ? "real" : "fake"} data
                      </Typography>
                      <Typography textAlign="center" fontStyle="italic" fontSize="8px">
                        Displaying {mocking ? "fake" : "real"} data
                      </Typography>
                    </Stack>
                  }
                >
                  {mocking ? <Article /> : <Cloud />}
                </SMTooltip>
              </IconButton>
            )}
            <PaletteModeToggle mode={mode} onChange={onPaletteModeChanged} />
            <IconButton
              onClick={handleAvatarClick}
              aria-controls={open ? "account-menu" : undefined}
              aria-haspopup="true"
              sx={{ p: 0 }}
              aria-expanded={open ? "true" : undefined}
            >
              <Avatar>{accounts[0].name?.charAt(0)}</Avatar>
            </IconButton>
          </Stack>
        </Toolbar>
        <Menu
          id="account-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          disableAutoFocusItem
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem onClick={signOut}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </AppBar>

      <Restrict to={DashboardPermissions} when={When.any}>
        <DashboardAppDrawer
          open={drawerOpen}
          handleDrawerClose={handleDrawerClose}
          handleDrawerOpen={handleDrawerOpen}
        />
      </Restrict>
    </Fragment>
  );
};

export default TopNavigation;
